import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import type { Part } from '@etnrlz/shared';
import { lookupPart, useAddMovement, useVehicles } from '../../lib/queries';
import { Badge, Button, Card, Field, Input, PageHeader, Select } from '../../components/ui';
import { Icon } from '../../components/icons';

export function ScannerPage() {
  const ref = useRef<Html5Qrcode | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<Part | null>(null);
  const [manual, setManual] = useState('');

  const stop = async () => {
    const inst = ref.current;
    if (inst && inst.isScanning) {
      try {
        await inst.stop();
      } catch {
        /* noop */
      }
    }
    setScanning(false);
  };

  const onResult = async (qrValue: string) => {
    await stop();
    try {
      const part = await lookupPart(qrValue);
      setFound(part);
      setError(null);
    } catch {
      setError(`Nenhuma peça para "${qrValue}".`);
    }
  };

  const start = async () => {
    setError(null);
    setFound(null);
    try {
      if (!ref.current) ref.current = new Html5Qrcode('qr-reader');
      setScanning(true);
      await ref.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (text) => void onResult(text),
        () => undefined,
      );
    } catch (e) {
      setScanning(false);
      setError(e instanceof Error ? e.message : 'Não foi possível acessar a câmera.');
    }
  };

  useEffect(() => () => void stop(), []);

  return (
    <div className="max-w-xl mx-auto">
      <PageHeader title="Escanear peça" subtitle="Aponte a câmera para o QR da etiqueta" />

      <Card className="text-center">
        <div
          id="qr-reader"
          className="mx-auto rounded-xl overflow-hidden bg-bg border border-border"
          style={{ maxWidth: 320 }}
        />
        {!scanning && !found && (
          <Button className="mt-4" size="lg" onClick={start}>
            <Icon name="scan" className="h-5 w-5" /> Iniciar câmera
          </Button>
        )}
        {scanning && (
          <Button className="mt-4" variant="secondary" onClick={stop}>
            Parar
          </Button>
        )}
        {error && <p className="text-st-warn text-sm mt-3">{error}</p>}

        <div className="mt-5 pt-5 border-t border-border">
          <Field label="Ou digite o código manualmente">
            <div className="flex gap-2">
              <Input
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="ETNRLZ-PART-…"
                onKeyDown={(e) => e.key === 'Enter' && manual && onResult(manual)}
              />
              <Button onClick={() => manual && onResult(manual)} disabled={!manual}>Buscar</Button>
            </div>
          </Field>
        </div>
      </Card>

      {found && <FoundPart part={found} onClear={() => setFound(null)} />}
    </div>
  );
}

function FoundPart({ part, onClear }: { part: Part; onClear: () => void }) {
  const add = useAddMovement(part.id);
  const { data: vehicles } = useVehicles();
  const [qtd, setQtd] = useState(1);
  const [vehicleId, setVehicleId] = useState('');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const low = part.quantidade < part.estoqueMinimo;

  const baixa = async () => {
    setErr(null);
    try {
      await add.mutateAsync({ tipo: 'SAIDA', quantidade: qtd, vehicleId: vehicleId || undefined, userId: 'u-celso', motivo: 'Baixa via scanner' });
      setDone(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erro');
    }
  };

  return (
    <Card className="mt-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-semibold text-lg">{part.nome}</div>
          <div className="text-xs text-muted num">{part.sku} · {part.localizacao ?? '—'}</div>
        </div>
        {low && <Badge color="#ef4444">Baixo</Badge>}
      </div>
      <div className="num text-3xl font-bold mt-2">{part.quantidade} <span className="text-sm text-muted font-normal">em estoque</span></div>

      {done ? (
        <div className="mt-4 text-st-done flex items-center gap-2">
          ✓ Baixa registrada.
          <Button size="sm" variant="ghost" onClick={onClear}>Escanear outra</Button>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 gap-3 items-end">
          <Field label="Quantidade (saída)">
            <Input type="number" min={1} value={qtd} onChange={(e) => setQtd(Number(e.target.value))} />
          </Field>
          <Field label="Para o carro">
            <Select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">—</option>
              {(vehicles ?? []).map((v) => (
                <option key={v.id} value={v.id}>{v.apelido}</option>
              ))}
            </Select>
          </Field>
          <div className="col-span-2 flex gap-2">
            <Button onClick={baixa} disabled={add.isPending} className="flex-1">Dar baixa</Button>
            <Button variant="secondary" onClick={onClear}>Cancelar</Button>
          </div>
          {err && <p className="text-st-danger text-sm col-span-2">{err}</p>}
        </div>
      )}
    </Card>
  );
}
